import { Box, Button, IconButton, Typography, CircularProgress } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useState, useRef } from 'react'
import { getConnection, uploadImage, deleteImage, getImageUrl } from '../../../services/salesforce'

export interface ImageUploadFieldProps {
  label: string
  imageContentDocumentId?: string
  onImageUpload: (contentDocumentId: string) => void
  onImageDelete: () => void
  onBlur?: () => void
  circular?: boolean // 円形表示オプション
  linkedEntityId?: string // リンク先レコードID（共有設定用: Career__c, Portfolio_Item__c, Highlight_Episode__c等）
}

export function ImageUploadField({
  label,
  imageContentDocumentId,
  onImageUpload,
  onImageDelete,
  onBlur,
  circular = false,
  linkedEntityId,
}: ImageUploadFieldProps) {
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // ファイルサイズチェック（5MB制限）
    if (file.size > 5 * 1024 * 1024) {
      setError('ファイルサイズは5MB以下にしてください')
      return
    }

    // ファイル形式チェック
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルのみアップロード可能です')
      return
    }

    setUploading(true)
    setError(null)

    try {
      // Base64エンコード
      const reader = new FileReader()
      reader.onload = async () => {
        try {
          const base64 = reader.result as string
          const base64Data = base64.split(',')[1]

          const conn = getConnection()
          if (!conn) {
            setError('認証が切れています')
            setUploading(false)
            return
          }

          const contentDocumentId = await uploadImage(conn, file.name, base64Data, linkedEntityId)
          console.log('Image uploaded, ContentDocumentId:', contentDocumentId)
          console.log('Calling onImageUpload with:', contentDocumentId)
          onImageUpload(contentDocumentId)
          console.log('onImageUpload called successfully')

          // input要素をリセット（同じファイルを再度選択できるように）
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }

          onBlur?.()
        } catch (err) {
          console.error('Upload error:', err)
          setError('アップロードに失敗しました')
        } finally {
          setUploading(false)
        }
      }
      reader.onerror = () => {
        setError('ファイルの読み込みに失敗しました')
        setUploading(false)
      }
      reader.readAsDataURL(file)
    } catch (err) {
      console.error('File read error:', err)
      setError('ファイルの読み込みに失敗しました')
      setUploading(false)
    }
  }

  const handleDelete = async () => {
    if (!imageContentDocumentId || deleting) return

    const conn = getConnection()
    if (!conn) {
      setError('認証が切れています')
      return
    }

    setDeleting(true)
    setError(null)

    try {
      await deleteImage(conn, imageContentDocumentId)
      onImageDelete()
      onBlur?.()
    } catch (err) {
      console.error('Delete error:', err)
      setError('削除に失敗しました')
    } finally {
      setDeleting(false)
    }
  }

  const previewUrl = imageContentDocumentId
    ? getImageUrl(getConnection()?.instanceUrl || '', imageContentDocumentId)
    : null

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <Button variant="outlined" component="label" disabled={uploading}>
        {uploading ? <CircularProgress size={24} /> : '画像をアップロード'}
        <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={handleFileUpload} />
      </Button>

      {error && (
        <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {previewUrl && (
        <Box sx={{ mt: 2, position: 'relative', width: '100%' }}>
          <img
            src={previewUrl}
            alt="Preview"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: 8,
              objectFit: 'cover',
              display: 'block',
              ...(circular && {
                aspectRatio: '1',
              }),
            }}
          />
          <IconButton
            onClick={handleDelete}
            disabled={deleting}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(200, 200, 200, 0.8)',
              },
            }}
          >
            {deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          </IconButton>
        </Box>
      )}
    </Box>
  )
}
